import type { ComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { Order } from './interfaces'
import type { PluralForms } from '@shared/lib/format'

import { OrderType } from '@brighterly/lib-core-types/orders/enums'

import { useListsStore } from '@shared/model'
import { withCount } from '@shared/lib/format'

export const ORDER_EMPTY_CELL = '—'

const CLASS_FORMS: PluralForms = { one: 'class', other: 'classes' }
const PACE_FORMS: PluralForms = { one: 'lesson/week', other: 'lessons/week' }

interface OrderOptionsReturn {
  statusOptions: ComputedRef<AppSelectOption[]>
  typeOptions: ComputedRef<AppSelectOption[]>
  getTypeLabel: (type: string) => string
  getDisciplineLabel: (code: string) => string
}

const toOptions = (record: Record<string, string>): AppSelectOption[] =>
  Object.entries(record).map(([value, text]) => ({ value, text }))

export const useOrderOptions = (): OrderOptionsReturn => {
  const listsStore = useListsStore()

  const statusMap = computed<Record<string, string>>(() => listsStore.getLists?.order?.status ?? {})
  const typeMap = computed<Record<string, string>>(() => listsStore.getLists?.order?.order_type ?? {})
  const disciplineMap = computed<Record<string, string>>(() => listsStore.getLists?.discipline?.code ?? {})

  const statusOptions = computed<AppSelectOption[]>(() => toOptions(statusMap.value))
  const typeOptions = computed<AppSelectOption[]>(() => toOptions(typeMap.value))

  const getTypeLabel = (type: string): string => typeMap.value[type] ?? type
  const getDisciplineLabel = (code: string): string => disciplineMap.value[code] ?? code

  return { statusOptions, typeOptions, getTypeLabel, getDisciplineLabel }
}

/** An upsell is named by its product; any other order by the lessons it bought. */
export const getOrderPackage = (order: Order): string => {
  const product = order.products[0]

  if (order.type === OrderType.UPSELL) return product?.name || ORDER_EMPTY_CELL

  const lessons = order.subscription ? order.subscription.lessons : (product?.lessons ?? 0)

  return withCount(lessons, CLASS_FORMS)
}

export const getOrderPace = (order: Order): string => {
  const pace = order.products[0]?.pace

  return pace ? withCount(pace, PACE_FORMS) : ORDER_EMPTY_CELL
}
